// =====================================================================
// CommandesPretesClients.jsx — Commandes prêtes v2 (16-10-2026) — PARAMÈTRES CLIENT
// =====================================================================
// Route /commande-prete/clients — administrateurs du module (commandes_pretes.admin.all : ADMIN et
// COORDINATOR, décision Silviu 14/10 ; droit propre, SPEC v3 D 17/10). SPEC-v2 §2.1, §3, §4, §5.
// La liste = le catalogue de clients existant (UmClient, « Clients mail » de
// Dimensions), complété avec les réglages du module :
//   - proposé dans « Commandes prêtes », transporteur (info, ex. 2701) ;
//   - mail « commande prête » : À / copie / réponse à, langues (au moins une,
//     dans l'ordre du mail), modèle par langue (vide = modèle par défaut, celui
//     d'aujourd'hui ; le néerlandais n'en a pas : sujet et texte obligatoires) ;
//   - mail au chargement (désactivé par défaut) et ses modèles ;
//   - aperçu du mail « commande prête » et du mail « chargée » (modèles et langues du
//     formulaire, même pas encore enregistrés ; destinataires = ceux enregistrés) ;
//   - lien client (page /client/<jeton>, sans compte) : créer / régénérer (le lien
//     n'est montré qu'UNE fois, avec « Copier ») et couper l'accès — confirmations
//     sur la page, jamais window.confirm.
// Un réglage impossible (aucune langue, adresse invalide…) n'est jamais
// enregistré : bouton grisé + message humain. Logique PURE : commande-prete/parametres.js.
// =====================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { readyClientSettings, periodLocks, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import { normClient } from './commande-prete/clients.js';
import { oublierClients } from './commande-prete/useClients.js';
import { dateHeureBxl } from './commande-prete/dates.js';
import {
  LANGUES_AVEC_DEFAUT, REPERES, REPERES_CHARGE, MAX_TRANSPORTEUR, formDepuisClient, corpsDepuisForm, formesDepuis,
  formModifie, validerForm, erreurDe, languesCochees, basculerLangue, deplacerLangue, nomLangue, texteDefaut,
  reperesInconnus, modeleEntame, reglageIncoherent, MSG_CLIENT_INACTIF,
  MAX_ADRESSES, maxAdressesAtteint, lignesEnErreur, adressesAcceptees, emailValide, ligneAdresseMaj, ligneAdresseAjoutee, ligneAdresseRetiree, lignesCollees, adressesCollees,
} from './commande-prete/parametres.js';
import { droitsCommandesPretes, texteAccesRefuse, DROIT_ADMIN } from './commande-prete/droits.js';
import { exempleStockage, joursEnEntier, prixEnCentimes, premierJourOuvert } from './commande-prete/stockage.js';
import { aujourdhuiBruxelles } from '../lib/dates.js';

const dateHeure = (d) => dateHeureBxl(d, { annee: true });
// Le lien client est-il actif ? (date du lien actuel, ou indicateur du serveur)
const lienActif = (c) => !!c && (c.portalActif ?? !!c.portalCreatedAt);
const NOUVEAU = '__nouveau__';
const VERS_LISTE = '__liste__';   // « quitter » vers la liste des commandes (fil d'Ariane)
const DESCR_REPERES = {
  ref: 'réf. de chargement', unites: '« 2 pallets and 1 parcel »', tableau: 'tableau des unités (SSCC, cm, kg)',
  photos: 'lien vers les photos', adresse: 'adresse de chargement',
};
const DESCR_REPERES_CHARGE = {
  ref: 'réf. de chargement', date: 'date et heure du départ', charge: 'unités chargées dans ce départ', quai: 'ce qui reste à quai',
};
const TYPES_APERCU = { pret: 'mail « commande prête »', charge: 'mail « chargée »' };

export default function CommandesPretesClients() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const autorise = droitsCommandesPretes(can).admin;

  const [liste, setListe] = useState(null);
  const [erreurListe, setErreurListe] = useState('');
  const [filtre, setFiltre] = useState('');
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [formes, setFormes] = useState({ langues: 'string', modeles: 'string' });
  const [vu, setVu] = useState(false);                  // tentative d'enregistrement : toutes les erreurs montrées
  const [enreg, setEnreg] = useState({ etat: 'repos', msg: '' });
  const [quitter, setQuitter] = useState(null);        // client demandé alors que le formulaire est modifié
  const [apercu, setApercu] = useState({ lang: null, type: 'pret', etat: 'repos', data: null, msg: '' });
  const [lien, setLien] = useState({ etat: 'repos', url: null, msg: '', question: null });
  const [copie, setCopie] = useState('');
  // v3 (A2) : projets actifs pour la facturation automatique (null = pas encore reçus)
  const [projets, setProjets] = useState(null);
  const [erreurProjets, setErreurProjets] = useState('');
  // Stockage sur dalle : mois clôturés (verrous actifs) — un « facturé à partir du » ne les touche pas (null = pas reçus)
  const [verrous, setVerrous] = useState(null);
  // Fiche ouverte À L'INSTANT (une réponse arrivée après un changement de client ne s'affiche jamais
  // sous un autre client : lien unique, aperçu, enregistrement).
  const selRef = useRef(null);
  const ouverteEncore = (id) => selRef.current === id;
  const modifieRef = useRef(false);   // formulaire modifié (lu à la fermeture de l'onglet)

  const charger = async () => {
    const l = await readyClientSettings.list();
    const tab = Array.isArray(l) ? l : (l && Array.isArray(l.clients) ? l.clients : []);
    setListe(tab);
    setFormes(formesDepuis(tab));
    return tab;
  };
  useEffect(() => {
    if (!autorise) return;
    charger().catch((e) => setErreurListe(errMsg(e)));
    readyClientSettings.projects()
      .then((l) => { setProjets(Array.isArray(l) ? l : []); setErreurProjets(''); })
      .catch((e) => setErreurProjets(errMsg(e)));
    // (échec : le serveur fait le même contrôle et le dit à l'enregistrement)
    periodLocks.listActive()
      .then((l) => setVerrous(new Set((Array.isArray(l) ? l : []).map((x) => x && x.period).filter(Boolean))))
      .catch(() => setVerrous(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorise]);

  const modifie = useMemo(() => !!form && !!initial && formModifie(form, initial), [form, initial]);
  modifieRef.current = modifie;
  const validation = useMemo(() => (form ? validerForm(form, liste || [], projets, aujourdhuiBruxelles(), verrous) : { erreurs: [], avertissements: [] }), [form, liste, projets, verrous]);
  const clientSel = liste && selId && selId !== NOUVEAU ? liste.find((c) => c.id === selId) : null;
  // Liste filtrée : recalculée quand la liste ou le filtre change (pas à chaque frappe dans un modèle).
  const visiblesMemo = useMemo(() => {
    const q = normClient(filtre);
    return (liste || [])
      .filter((c) => !q || normClient(c.name).includes(q))
      .sort((a, b) => (!!b.readyEnabled - !!a.readyEnabled) || String(a.name).localeCompare(String(b.name), 'fr', { sensitivity: 'base' }));
  }, [liste, filtre]);

  // ---------- Sélection ----------
  const ouvrirFiche = (id, tab = liste, { garderLien = false } = {}) => {
    if (id === VERS_LISTE) { navigate('/commande-prete'); return; }
    const c = id === NOUVEAU ? { isActive: true, readyEnabled: true } : (tab || []).find((x) => x.id === id);
    if (!c) return;
    const memeFiche = selRef.current === id;
    selRef.current = id;
    setSelId(id);
    setForm(formDepuisClient(c));
    // Référence = tel que stocké : un réglage hérité corrigé à l'écran compte comme une modification.
    setInitial(formDepuisClient(c, { normaliser: false }));
    setVu(false);
    setEnreg({ etat: 'repos', msg: '' });
    setQuitter(null);
    setApercu({ lang: null, type: 'pret', etat: 'repos', data: null, msg: '' });
    // Le lien affiché UNE fois reste à l'écran après un enregistrement de la même fiche.
    if (!(garderLien && memeFiche)) {
      setLien({ etat: 'repos', url: null, msg: '', question: null });
      setCopie('');
    }
  };
  // Formulaire retouché : un aperçu déjà affiché ne le montre plus — il disparaît.
  useEffect(() => {
    setApercu((a) => (a.etat === 'repos' ? a : { lang: null, type: 'pret', etat: 'repos', data: null, msg: '' }));
  }, [form]);
  // Formulaire modifié : fermer / recharger l'onglet demande confirmation au navigateur.
  useEffect(() => {
    const avant = (e) => { if (modifieRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', avant);
    return () => window.removeEventListener('beforeunload', avant);
  }, []);
  // Un formulaire modifié ne se perd pas sans le dire (question sur la page).
  const demander = (id) => {
    if (id === selId) return;
    if (modifie) { setQuitter(id); return; }
    ouvrirFiche(id);
  };

  // ---------- Champs ----------
  const majForm = (fn) => { setForm(fn); setEnreg({ etat: 'repos', msg: '' }); };
  const maj = (champ, v) => majForm((f) => ({ ...f, [champ]: v }));
  // « ＋ Ajouter une adresse » : le curseur va sur la ligne vide du champ (celle qui attend, ou la nouvelle).
  const blocAdresses = useRef(null);
  const allerALigne = (k) => {
    const l = blocAdresses.current ? [...blocAdresses.current.querySelectorAll(`[data-adresses="${k}"] input`)] : [];
    const cible = l.find((x) => !x.value.trim()) || l[l.length - 1];
    if (cible) cible.focus();
  };
  // Client désactivé : ses réglages restent tels quels (revue 16/10 : rien n'est modifié en silence) —
  // inactif, il n'est proposé nulle part ; réactivé, il l'est de nouveau s'il est « Proposé ».
  const majActif = (v) => {
    setForm((f) => ({ ...f, isActive: v }));
    setEnreg({ etat: 'repos', msg: '' });
  };
  // Plus proposé dans « Commandes prêtes » : il ne se charge plus, donc plus de mail au chargement.
  const majPropose = (v) => {
    setForm((f) => ({ ...f, readyEnabled: v, loadMailEnabled: v ? f.loadMailEnabled : false }));
    setEnreg({ etat: 'repos', msg: '' });
  };
  // 22-10-2026 (décision Silviu) : l'automatisation est ACTIVE par défaut — choisir un projet de
  // facturation là où il n'y en avait pas coche la case ; un admin peut la décocher, elle le reste.
  // Plus de projet : l'automatisation (qui l'exige) se décoche, sinon l'enregistrement serait refusé.
  // Le stockage sur dalle, lui, reste coché (revue 22/10 : le décocher effacerait le tarif à l'enregistrement, même
  // si le projet est rechoisi aussitôt) — l'écran dit tout de suite qu'il faut un projet, l'enregistrement attend.
  const majProjet = (v) => {
    setForm((f) => ({ ...f, projectId: v, autoFacturation: !v ? false : (!f.projectId ? true : f.autoFacturation) }));
    setEnreg({ etat: 'repos', msg: '' });
  };
  // Stockage sur dalle coché : « facturé à partir du » vide → aujourd'hui (à Bruxelles), ou le 1er du premier mois
  // ouvert si le mois en cours est clôturé ; modifiable.
  const majStockage = (v) => {
    setForm((f) => ({ ...f, stockageActif: v, stockageDepuis: v && !f.stockageDepuis ? premierJourOuvert(aujourdhuiBruxelles(), verrous) : f.stockageDepuis }));
    setEnreg({ etat: 'repos', msg: '' });
  };
  const majModele = (cle, l, champ, v) => {
    setForm((f) => ({ ...f, [cle]: { ...f[cle], [l]: { subject: '', body: '', ...(f[cle][l] || {}), [champ]: v } } }));
    setEnreg({ etat: 'repos', msg: '' });
  };
  const viderModele = (cle, l) => { setForm((f) => { const m = { ...f[cle] }; delete m[l]; return { ...f, [cle]: m }; }); setEnreg({ etat: 'repos', msg: '' }); };

  // ---------- Enregistrer ----------
  const enregistrer = async () => {
    setVu(true);
    if (validation.erreurs.length || enreg.etat === 'encours' || !modifie) return;
    const fiche = selId;
    setEnreg({ etat: 'encours', msg: '' });
    let enregistre;
    try {
      const corps = corpsDepuisForm(form, formes);
      enregistre = form.id ? await readyClientSettings.update(form.id, corps) : await readyClientSettings.create(corps);
    } catch (e) {
      if (ouverteEncore(fiche)) setEnreg({ etat: 'err', msg: errMsg(e) });
      return;
    }
    // ENREGISTRÉ (le serveur a répondu) : la suite ne peut plus faire croire à un échec.
    oublierClients();   // champ « Client » des autres écrans : la liste sera relue
    let tab = liste || [];
    if (enregistre && enregistre.id) tab = [...tab.filter((c) => c.id !== enregistre.id), enregistre];
    try { tab = await charger(); } catch { setListe(tab); }   // relecture ratée : la réponse du serveur suffit
    if (!ouverteEncore(fiche)) return;   // un autre client a été ouvert entre-temps : il reste affiché
    const id = (enregistre && enregistre.id) || form.id;
    if (id) {
      selRef.current = id;   // client créé : c'est la même fiche (son lien éventuel reste)
      ouvrirFiche(id, tab, { garderLien: true });
    }
    setEnreg({ etat: 'ok', msg: 'Enregistré ✓' });
  };
  // Revenir à la fiche telle qu'à l'ouverture (réglages hérités contradictoires déjà corrigés, jamais
  // remis tels quels) ; le lien affiché une fois reste.
  const annulerModifs = () => { if (selId) ouvrirFiche(selId, liste, { garderLien: true }); };

  // ---------- Aperçu : modèles et langues du FORMULAIRE (pas encore enregistrés), commande exemple ----------
  const voirApercu = async (type, lang) => {
    if (!form || !form.id) return;
    const entame = modeleEntame((type === 'pret' ? form.modeles : form.modelesCharge)[lang]);
    if (entame) {
      setApercu({ lang, type, etat: 'err', data: null, msg: `modèle ${nomLangue(lang).toLowerCase()} incomplet — remplissez le sujet ET le texte (ou videz-les pour le modèle par défaut)` });
      return;
    }
    const fiche = selId;
    setApercu({ lang, type, etat: 'encours', data: null, msg: '' });
    try {
      const c = corpsDepuisForm(form, formes);
      const r = await readyClientSettings.preview(form.id, lang, {
        type, mailLanguages: c.mailLanguages, mailTemplates: c.mailTemplates, loadMailTemplates: c.loadMailTemplates,
        // les adresses du formulaire, même vides — sauf un champ que le serveur refuserait (adresse encore incomplète,
        // trop d'adresses) : l'aperçu y garde celles enregistrées
        ...Object.fromEntries(['readyTo', 'readyCc', 'readyReplyTo'].filter((x) => adressesAcceptees(form[x])).map((x) => [x, c[x]])),
      });
      if (ouverteEncore(fiche)) setApercu({ lang, type, etat: 'ok', data: r || {}, msg: '' });
    } catch (e) {
      if (ouverteEncore(fiche)) setApercu({ lang, type, etat: 'err', data: null, msg: errMsg(e) });
    }
  };

  // ---------- Lien client ----------
  const genererLien = async () => {
    if (!form || !form.id) return;
    const fiche = selId;
    setLien({ etat: 'encours', url: null, msg: '', question: null });
    setCopie('');
    try {
      const r = await readyClientSettings.portalCreate(form.id);
      const url = r && r.url ? (String(r.url).startsWith('/') ? `${window.location.origin}${r.url}` : String(r.url)) : null;
      if (!url) throw new Error('Réponse inattendue du serveur (pas de lien)');
      charger().catch(() => {});   // date du lien à jour dans la liste ; le formulaire n'est pas touché
      // Le lien (montré UNE fois) ne s'affiche que sous SON client.
      if (ouverteEncore(fiche)) setLien({ etat: 'ok', url, msg: '', question: null });
    } catch (e) {
      if (ouverteEncore(fiche)) setLien({ etat: 'err', url: null, msg: errMsg(e), question: null });
    }
  };
  const couperLien = async () => {
    if (!form || !form.id) return;
    const fiche = selId;
    setLien({ etat: 'encours', url: null, msg: '', question: null });
    setCopie('');
    try {
      await readyClientSettings.portalDelete(form.id);
      charger().catch(() => {});
      if (ouverteEncore(fiche)) setLien({ etat: 'coupe', url: null, msg: 'Accès coupé : l\'ancien lien ne marche plus.', question: null });
    } catch (e) {
      if (ouverteEncore(fiche)) setLien({ etat: 'err', url: null, msg: errMsg(e), question: null });
    }
  };
  const copier = async () => {
    const url = lien.url;
    if (!url) return;
    try {
      if (!navigator.clipboard || !window.isSecureContext) throw new Error('presse-papiers indisponible');
      await navigator.clipboard.writeText(url);
      setCopie('Lien copié ✓');
    } catch {
      // Repli : le lien est sélectionné dans son champ (copie classique du navigateur).
      const el = document.getElementById('lienClient');
      let ok = false;
      if (el) {
        el.focus();
        el.select();
        try { ok = document.execCommand('copy'); } catch { ok = false; }
      }
      setCopie(ok ? 'Lien copié ✓' : 'Copie automatique impossible : le lien est sélectionné — faites Ctrl+C (ou appui long → Copier).');
    }
  };

  // =====================================================================
  // Rendu
  // =====================================================================
  if (!autorise) {
    return <div className="pcl"><style>{CSS}</style><p className="note err">{texteAccesRefuse(DROIT_ADMIN)}</p></div>;
  }

  const visibles = visiblesMemo;
  const err = (champ) => (vu || champ === 'langues' || champ.startsWith('modele') ? erreurDe(validation, champ) : null);
  const cochees = form ? languesCochees(form) : [];
  const nomQuitter = quitter === NOUVEAU ? 'le nouveau client' : quitter === VERS_LISTE ? 'la liste des commandes'
    : ((liste || []).find((c) => c.id === quitter) || {}).name;

  const editeurModeles = (cle, titre, defautsTexte) => cochees.map((l) => {
    const m = form[cle][l] || { subject: '', body: '' };
    const videM = !m.subject.trim() && !m.body.trim();
    const e = erreurDe(validation, `${cle}.${l}`);
    const inconnus = reperesInconnus(`${m.subject}\n${m.body}`, cle === 'modeles' ? REPERES : REPERES_CHARGE);
    return (
      <fieldset className={'modele' + (e ? ' bad' : '')} key={`${cle}-${l}`}>
        <legend>{`${titre} — ${nomLangue(l)}`}</legend>
        <span className={'etat-modele' + (videM ? (LANGUES_AVEC_DEFAUT.includes(l) ? ' defaut' : ' manque') : ' perso')}>
          {videM ? (LANGUES_AVEC_DEFAUT.includes(l) ? '● Modèle par défaut' : '● Modèle à écrire') : '● Modèle personnalisé'}
        </span>
        <label htmlFor={`${cle}-${l}-s`}>Sujet</label>
        <input id={`${cle}-${l}-s`} type="text" value={m.subject} placeholder={LANGUES_AVEC_DEFAUT.includes(l) ? 'Vide = sujet par défaut' : 'Obligatoire'}
          onChange={(ev) => majModele(cle, l, 'subject', ev.target.value)} />
        <label htmlFor={`${cle}-${l}-b`}>Texte</label>
        <textarea id={`${cle}-${l}-b`} rows={7} value={m.body} placeholder={LANGUES_AVEC_DEFAUT.includes(l) ? defautsTexte[l] : 'Obligatoire'}
          onChange={(ev) => majModele(cle, l, 'body', ev.target.value)} />
        <span className="note">{texteDefaut(l)}</span>
        {inconnus.length > 0 && <span className="note warn">{`Repère${inconnus.length > 1 ? 's' : ''} inconnu${inconnus.length > 1 ? 's' : ''} : ${inconnus.join(', ')} — laissé${inconnus.length > 1 ? 's' : ''} tel${inconnus.length > 1 ? 's' : ''} quel${inconnus.length > 1 ? 's' : ''} dans le mail.`}</span>}
        {e && <span className="note err">{e}</span>}
        {!videM && LANGUES_AVEC_DEFAUT.includes(l) && (
          <button type="button" className="btn small" onClick={() => viderModele(cle, l)}>Revenir au modèle par défaut</button>
        )}
      </fieldset>
    );
  });

  const enregistrable = !!form && modifie && !validation.erreurs.length && enreg.etat !== 'encours';
  const actifLien = lienActif(clientSel);

  return (
    <div className="pcl">
      <style>{CSS}</style>
      <Breadcrumbs items={[{ label: 'Commandes prêtes', onClick: () => (modifie ? setQuitter(VERS_LISTE) : navigate('/commande-prete')) }, { label: 'Paramètres clients' }]} />
      <h1>⚙ Paramètres clients</h1>
      <p className="note">Les clients de Zeus (catalogue « Clients mail ») et leurs réglages pour « Commandes prêtes » : destinataires, langues et texte du mail, mail au chargement, lien de la page client.</p>

      <div className="grille">
        <aside className="liste">
          <input type="text" className="filtre" placeholder="Rechercher un client…" value={filtre} aria-label="Rechercher un client"
            onChange={(e) => setFiltre(e.target.value)} />
          <button type="button" className="btn" onClick={() => demander(NOUVEAU)}>＋ Nouveau client</button>
          {erreurListe && <span className="note err">{`Liste indisponible : ${erreurListe}`}</span>}
          {!erreurListe && liste === null && <span className="note">Chargement…</span>}
          {liste && !visibles.length && <span className="note">{liste.length ? 'Aucun client ne correspond.' : 'Aucun client : créez-en un.'}</span>}
          <ul>
            {visibles.map((c) => (
              <li key={c.id}>
                <button type="button" className={'ligne' + (c.id === selId ? ' sel' : '')} onClick={() => demander(c.id)} aria-current={c.id === selId ? 'true' : undefined}>
                  <b>{c.name}</b>
                  <span className="tags">
                    {c.isActive === false && <span className="tag gris">inactif</span>}
                    {c.readyEnabled && c.isActive !== false ? <span className="tag vert">Commandes prêtes</span> : <span className="tag gris">non proposé</span>}
                    {c.carrierCode ? <span className="tag">{c.carrierCode}</span> : null}
                    {lienActif(c) && <span className="tag bleu">lien client</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="fiche">
          {quitter && (
            <div className="question" role="alert">
              <span>{`Modifications non enregistrées : les abandonner pour ouvrir ${nomQuitter || 'ce client'} ?`}</span>
              <div className="row">
                <button type="button" className="btn danger" onClick={() => ouvrirFiche(quitter)}>Abandonner les modifications</button>
                <button type="button" className="btn" onClick={() => setQuitter(null)}>Rester ici</button>
              </div>
            </div>
          )}
          {!form && <p className="note">Choisissez un client dans la liste, ou créez-en un.</p>}
          {form && (
            // Pendant l'enregistrement, le formulaire est figé : la réponse du serveur le remplace,
            // une frappe faite entre-temps serait perdue.
            <fieldset className="pcl-form" disabled={enreg.etat === 'encours'}>
              <h2>{form.id ? form.name || '—' : 'Nouveau client'}</h2>
              {reglageIncoherent(initial) && !reglageIncoherent(form) && modifie && (
                <div className="msg warn" id="pc-corrige">Réglage incohérent corrigé à l'écran (client non proposé : pas de mail au chargement) — enregistrez pour l'appliquer.</div>
              )}

              <fieldset className="bloc">
                <legend>Client</legend>
                <label htmlFor="pc-nom">Nom</label>
                <input id="pc-nom" type="text" value={form.name} onChange={(e) => maj('name', e.target.value)} className={err('name') ? 'bad' : ''} />
                {err('name') && <span className="note err">{err('name')}</span>}
                <label className="case"><input type="checkbox" checked={form.isActive} onChange={(e) => majActif(e.target.checked)} /> Client actif</label>
                {/* Inactif : « Proposé » reste tel quel (revue 16/10) — il ne vaut qu'une fois le client réactivé. */}
                <label className="case">
                  <input type="checkbox" checked={form.readyEnabled} onChange={(e) => majPropose(e.target.checked)} />
                  Proposé dans « Commandes prêtes » (encodage et chargement)
                </label>
                {!form.isActive && <span className="note warn" id="pc-inactif">{MSG_CLIENT_INACTIF}</span>}
                <label htmlFor="pc-transp">Transporteur (info affichée à l'encodage, ex. 2701)</label>
                <input id="pc-transp" type="text" value={form.carrierCode} maxLength={MAX_TRANSPORTEUR} className={err('carrierCode') ? 'bad' : ''}
                  onChange={(e) => maj('carrierCode', e.target.value)} />
                {err('carrierCode') && <span className="note err">{err('carrierCode')}</span>}
              </fieldset>

              <fieldset className="bloc" ref={blocAdresses}>
                <legend>Mail « commande prête »</legend>
                <span className="note">Une adresse par ligne — « ＋ Ajouter une adresse » pour une personne de plus. (Distinct des adresses du mail par mesure de « Dimensions ».)</span>
                {[['readyTo', 'Destinataires (À)'], ['readyCc', 'Copie (CC)'], ['readyReplyTo', 'Réponse à']].map(([k, lbl]) => {
                  const enErreur = err(k) ? lignesEnErreur(form[k]) : null;
                  const max = maxAdressesAtteint(form[k]);
                  return (
                  <div className="field" key={k} role="group" aria-labelledby={`pc-${k}-titre`} data-adresses={k}>
                    <span className="lbl" id={`pc-${k}-titre`}>{lbl}</span>
                    {form[k].map((x, i) => (
                      <div className="ligne-adresse" key={i}>
                        <input id={i === 0 ? `pc-${k}` : undefined} type="text" inputMode="email" autoComplete="off" spellCheck={false} value={x}
                          aria-label={`${lbl} — adresse ${i + 1}`}
                          className={'mono' + (enErreur && enErreur[i] ? ' bad' : '')}
                          onChange={(e) => majForm((f) => ligneAdresseMaj(f, k, i, e.target.value))}
                          onPaste={(e) => {
                            // des adresses collées (« a, b », « Nom <a> », « a (Nom) », « mailto:a » copiés) : une ligne par adresse ;
                            // le texte de la ligne hors sélection garde la sienne (jamais collé à une adresse) ;
                            // une adresse simple dans une ligne vide, un morceau d'adresse (« @brain-log.com »), rien de reconnaissable : collage normal
                            const colle = e.clipboardData.getData('text');
                            const d = adressesCollees(colle);
                            const el = e.currentTarget;
                            const reste = (el.value.slice(0, el.selectionStart ?? el.value.length) + el.value.slice(el.selectionEnd ?? el.value.length)).trim();
                            if (!d.some(emailValide) || (d.length === 1 && d[0] === colle.trim() && !reste)) return;
                            e.preventDefault();
                            majForm((f) => lignesCollees(f, k, i, reste ? [reste, ...d] : d));
                          }} />
                        <button type="button" className="btn small" title="Retirer cette adresse" aria-label={`Retirer l'adresse ${i + 1} de « ${lbl} »`}
                          disabled={form[k].length === 1 && !x} onClick={() => majForm((f) => ligneAdresseRetiree(f, k, i))}>✕</button>
                      </div>
                    ))}
                    <button type="button" className="btn small ajouter" disabled={max}
                      onClick={() => {
                        const g = ligneAdresseAjoutee(form, k);
                        if (g !== form) flushSync(() => majForm(() => g));   // la nouvelle ligne est affichée…
                        allerALigne(k);   // … et reçoit le curseur (ou celle, vide, qui attendait déjà)
                      }}>＋ Ajouter une adresse</button>
                    {max && <span className="note">{`${MAX_ADRESSES} adresses au plus.`}</span>}
                    {err(k) && <span className="note err">{err(k)}</span>}
                  </div>
                  );
                })}

                <span className="lbl">Langues du mail (dans l'ordre du mail)</span>
                <ul className="langues">
                  {form.langues.map((x, i) => (
                    <li key={x.l}>
                      <label className="case"><input type="checkbox" checked={x.on} onChange={() => { setForm((f) => basculerLangue(f, x.l)); setEnreg({ etat: 'repos', msg: '' }); }} /> {nomLangue(x.l)}</label>
                      <span className="fleches">
                        <button type="button" className="btn small" aria-label={`Monter ${nomLangue(x.l)}`} disabled={i === 0} onClick={() => { setForm((f) => deplacerLangue(f, x.l, -1)); setEnreg({ etat: 'repos', msg: '' }); }}>↑</button>
                        <button type="button" className="btn small" aria-label={`Descendre ${nomLangue(x.l)}`} disabled={i === form.langues.length - 1} onClick={() => { setForm((f) => deplacerLangue(f, x.l, 1)); setEnreg({ etat: 'repos', msg: '' }); }}>↓</button>
                      </span>
                    </li>
                  ))}
                </ul>
                {cochees.length > 0 && <span className="note">{`Le mail part en ${cochees.map((l) => nomLangue(l).toLowerCase()).join(' puis ')}.`}</span>}
                {err('langues') && <span className="note err" role="alert">{err('langues')}</span>}

                <details className="aide">
                  <summary>Repères remplis par Zeus</summary>
                  <ul>{REPERES.map((r) => <li key={r}><code>{`{${r}}`}</code> — {DESCR_REPERES[r]}</li>)}</ul>
                  <span className="note">Le texte est envoyé tel quel (le HTML n'est pas interprété). Un repère inconnu reste tel quel et est signalé.
                    {' '}<code>{'{tableau}'}</code> et <code>{'{photos}'}</code> forment leur propre bloc : écrits au milieu d'une phrase, ils passent entre deux paragraphes.</span>
                </details>
                {editeurModeles('modeles', 'Mail « commande prête »', {
                  en: 'Vide = le mail anglais actuel (« Hello, Your order is ready for collection… »)',
                  fr: 'Vide = le mail français actuel',
                })}
              </fieldset>

              <fieldset className="bloc">
                <legend>Mail au chargement</legend>
                {/* Seulement pour un client proposé (sinon il ne se charge jamais) : grisée sinon — sauf si elle
                    est cochée (réglage hérité) : elle reste décochable, l'erreur dit pourquoi. */}
                <label className={'case' + (!form.readyEnabled ? ' grise' : '')}>
                  <input type="checkbox" checked={form.loadMailEnabled} disabled={!form.readyEnabled && !form.loadMailEnabled}
                    onChange={(e) => maj('loadMailEnabled', e.target.checked)} />
                  Prévenir le client par mail à chaque départ validé (unités chargées + ce qui reste à quai)
                </label>
                {!form.readyEnabled && <span className="note">Client non proposé dans « Commandes prêtes » : il ne se charge pas, pas de mail au chargement.</span>}
                {erreurDe(validation, 'loadMailEnabled') && <span className="note err">{erreurDe(validation, 'loadMailEnabled')}</span>}
                {!form.loadMailEnabled && <span className="note">Désactivé : le client voit le chargement sur sa page, sans mail.</span>}
                {form.loadMailEnabled && (
                  <details className="aide">
                    <summary>Repères du mail « chargée »</summary>
                    <ul>{REPERES_CHARGE.map((r) => <li key={r}><code>{`{${r}}`}</code> — {DESCR_REPERES_CHARGE[r]}</li>)}</ul>
                    <span className="note"><code>{'{charge}'}</code> et <code>{'{quai}'}</code> forment leur propre bloc : écrits au milieu d'une phrase, ils passent entre deux paragraphes.</span>
                  </details>
                )}
                {form.loadMailEnabled && editeurModeles('modelesCharge', 'Mail « chargée »', {
                  en: 'Vide = « Your order {ref} has been loaded on {date}. » + unités chargées + « Still at our dock: … »',
                  fr: 'Vide = « Votre commande {ref} a été chargée le {date}. » + « Encore à quai : … »',
                })}
              </fieldset>

              <fieldset className="bloc">
                <legend>Facturation automatique</legend>
                <span className="note">Au 1er mail « prête » : filmage (FILMAGE_PAL, une ligne par palette, jamais les colis), ajusté si un mail de correction change le nombre de palettes. À « Valider le départ » : palettes sorties (type EURO). Jamais pour une commande de test. Active par défaut dès qu'un projet est choisi : décochez pour l'arrêter (et prévenez l'équipe, qui reprend alors l'encodage à la main).</span>
                <label htmlFor="pc-projet">Projet de facturation</label>
                <select id="pc-projet" value={form.projectId} className={erreurDe(validation, 'projectId') ? 'bad' : ''}
                  onChange={(e) => majProjet(e.target.value)}>
                  <option value="">— Aucun projet —</option>
                  {/* Projet enregistré absent de la liste : dit tel quel (jamais « Aucun projet » à sa place) */}
                  {form.projectId && !(projets || []).some((x) => x.id === form.projectId) && (
                    <option value={form.projectId}>{projets ? 'Projet inactif ou supprimé (à remplacer)' : erreurProjets ? 'Projet enregistré (liste des projets indisponible)' : 'Projet enregistré (chargement de la liste…)'}</option>
                  )}
                  {(projets || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                {erreurProjets && <span className="note err">{`Liste des projets indisponible (${erreurProjets}) : rechargez la page.`}</span>}
                {/* Sans projet : la case est grisée — sauf si elle est cochée (elle reste décochable, l'erreur dit pourquoi). */}
                <label className={'case' + (!form.projectId ? ' grise' : '')}>
                  <input type="checkbox" checked={form.autoFacturation} disabled={!form.projectId && !form.autoFacturation}
                    onChange={(e) => maj('autoFacturation', e.target.checked)} />
                  Automatisation active (filmage + palettes)
                </label>
                {!form.projectId && !form.autoFacturation && <span className="note">Choisissez d'abord le projet de facturation pour activer l'automatisation.</span>}
                {erreurDe(validation, 'projectId') && <span className="note err" role="alert">{erreurDe(validation, 'projectId')}</span>}
              </fieldset>

              <fieldset className="bloc">
                <legend>Stockage sur dalle</legend>
                <span className="note">Palettes encore sur la dalle après les jours gratuits (comptés depuis le 1er mail « prête ») : prix par palette et par jour, calculé à l'heure (20 j 18 h = 20,75 j). Jamais pour une commande de test. Luca le voit dans Facturation (par mois, détail par commande) ; à la fin de chaque mois, une ligne par commande s'écrit dans Prélèvements (STOCKAGE_DALLE, en heures-palette).</span>
                {/* Sans projet : la case est grisée — sauf si elle est cochée (elle reste décochable, l'erreur dit pourquoi). */}
                <label className={'case' + (!form.projectId ? ' grise' : '')}>
                  <input type="checkbox" checked={form.stockageActif} disabled={!form.projectId && !form.stockageActif}
                    onChange={(e) => majStockage(e.target.checked)} />
                  Facturer le stockage sur dalle
                </label>
                {!form.projectId && !form.stockageActif && <span className="note">Choisissez d'abord le projet de facturation (plus haut).</span>}
                {/* Dit tout de suite (pas seulement à l'enregistrement) : projet retiré alors que la case est cochée. */}
                {erreurDe(validation, 'stockage') && <span className="note err" role="alert">{erreurDe(validation, 'stockage')}</span>}
                {form.stockageActif && (
                  <>
                    <label htmlFor="pc-stock-franchise">Jours gratuits par palette (0 = facturé dès la 1re heure)</label>
                    <input id="pc-stock-franchise" type="text" inputMode="numeric" autoComplete="off" value={form.stockageFranchise}
                      className={err('stockageFranchise') ? 'bad' : ''} onChange={(e) => maj('stockageFranchise', e.target.value)} />
                    {err('stockageFranchise') && <span className="note err">{err('stockageFranchise')}</span>}
                    <label htmlFor="pc-stock-prix">Prix par palette et par jour (€)</label>
                    <input id="pc-stock-prix" type="text" inputMode="decimal" autoComplete="off" placeholder="ex. 2,50" value={form.stockagePrix}
                      className={err('stockagePrix') ? 'bad' : ''} onChange={(e) => maj('stockagePrix', e.target.value)} />
                    {err('stockagePrix') && <span className="note err">{err('stockagePrix')}</span>}
                    <label htmlFor="pc-stock-depuis">Facturé à partir du</label>
                    <input id="pc-stock-depuis" type="date" value={form.stockageDepuis}
                      className={err('stockageDepuis') ? 'bad' : ''} onChange={(e) => maj('stockageDepuis', e.target.value)} />
                    {err('stockageDepuis') && <span className="note err">{err('stockageDepuis')}</span>}
                    <span className="note">Les jours gratuits comptent depuis l'arrivée sur la dalle, même avant cette date ; seules les heures à partir de cette date sont facturées. Avant d'activer : une palette partie sans être scannée au chargement reste « à quai » et serait facturée — chargez-la d'abord.</span>
                    <span className="note">Le tarif en vigueur à la fin d'un mois s'applique à tout ce mois ; un mois terminé garde son tarif (un changement ou un arrêt vaut pour le mois en cours et les suivants). Un tarif posé après la fin d'un mois vaut pour ce mois jusqu'à sa clôture (verrouillage dans Facturation) : une faute de saisie corrigée avant ne reste pas. Un mois verrouillé garde ses prix, même déverrouillé ensuite.</span>
                    {exempleStockage(joursEnEntier(form.stockageFranchise), prixEnCentimes(form.stockagePrix)) && (
                      <span className="note ok">{exempleStockage(joursEnEntier(form.stockageFranchise), prixEnCentimes(form.stockagePrix))}</span>
                    )}
                  </>
                )}
              </fieldset>

              {/* (Les repères inconnus sont signalés sous chaque modèle : pas en double ici.) */}
              {validation.avertissements.filter((a) => !/repères? inconnus?/.test(a)).length > 0 && (
                <div className="msg warn">{validation.avertissements.filter((a) => !/repères? inconnus?/.test(a)).map((a) => <div key={a}>⚠ {a}</div>)}</div>
              )}
              {vu && validation.erreurs.length > 0 && (
                <div className="msg err" role="alert">
                  <b>Pas encore enregistrable :</b>
                  <ul>{validation.erreurs.map((x) => <li key={x.champ}>{x.msg}</li>)}</ul>
                </div>
              )}
              <div className="row barre">
                <button type="button" className={'btn primary' + (enregistrable ? '' : ' inactif')} aria-disabled={!enregistrable} onClick={enregistrer}>
                  {enreg.etat === 'encours' ? '⏳ Enregistrement…' : form.id ? 'Enregistrer' : 'Créer le client'}
                </button>
                <button type="button" className="btn" disabled={!modifie || enreg.etat === 'encours'} onClick={annulerModifs}>Annuler les modifications</button>
              </div>
              {!modifie && enreg.etat !== 'ok' && form.id && <span className="note">Aucune modification.</span>}
              {modifie && validation.erreurs.length > 0 && !vu && <span className="note err">{validation.erreurs[0].msg}</span>}
              {enreg.msg && <span className={'note' + (enreg.etat === 'err' ? ' err' : ' ok')} role="status">{enreg.msg}</span>}

              {form.id ? (
                <fieldset className="bloc">
                  <legend>Aperçu du mail</legend>
                  <span className="note">Avec une commande exemple, les langues et les textes ci-dessus (même pas encore enregistrés) ; destinataires = ceux enregistrés.</span>
                  {['pret', ...(form.loadMailEnabled ? ['charge'] : [])].map((type) => (
                    <div className="row" key={type}>
                      {cochees.map((l) => (
                        <button type="button" key={l} className="btn small" disabled={apercu.etat === 'encours'} onClick={() => voirApercu(type, l)}>
                          {`Aperçu ${TYPES_APERCU[type]} — ${nomLangue(l).toLowerCase()}`}
                        </button>
                      ))}
                    </div>
                  ))}
                  {!cochees.length && <span className="note err">Cochez au moins une langue pour voir l'aperçu.</span>}
                  {apercu.etat === 'encours' && <span className="note">Préparation de l'aperçu…</span>}
                  {apercu.etat === 'err' && <span className="note err">{`Aperçu impossible : ${apercu.msg}`}</span>}
                  {apercu.etat === 'ok' && apercu.data && <Apercu data={apercu.data} lang={apercu.lang} type={apercu.type} />}
                </fieldset>
              ) : <span className="note">L'aperçu du mail et le lien client sont disponibles une fois le client créé.</span>}

              {form.id && (
                <fieldset className="bloc">
                  <legend>Lien de la page client (sans compte)</legend>
                  <span className="note">
                    {actifLien
                      ? `Lien actif${clientSel && clientSel.portalCreatedAt ? ` depuis le ${dateHeure(clientSel.portalCreatedAt)}` : ''}. Le client y voit ses commandes des 90 derniers jours (jamais les commandes de test).`
                      : 'Aucun lien actif : le client n\'a pas accès à sa page.'}
                  </span>
                  {lien.url && (
                    <div className="lien" role="status">
                      <b>Nouveau lien — il ne sera plus jamais affiché : copiez-le maintenant.</b>
                      <div className="row">
                        <input id="lienClient" type="text" readOnly value={lien.url} className="mono" onFocus={(e) => e.target.select()} />
                        <button type="button" className="btn primary" onClick={copier}>Copier</button>
                      </div>
                      {copie && <span className="note">{copie}</span>}
                    </div>
                  )}
                  {lien.question === 'regenerer' && (
                    <div className="question" role="alert">
                      <span>Régénérer le lien : l'ancien lien ne marchera plus (à renvoyer au client). Continuer ?</span>
                      <div className="row">
                        <button type="button" className="btn danger" onClick={genererLien}>Oui, régénérer</button>
                        <button type="button" className="btn" onClick={() => setLien((x) => ({ ...x, question: null }))}>Annuler</button>
                      </div>
                    </div>
                  )}
                  {lien.question === 'couper' && (
                    <div className="question" role="alert">
                      <span>Couper l'accès : le lien actuel ne marchera plus. Continuer ?</span>
                      <div className="row">
                        <button type="button" className="btn danger" onClick={couperLien}>Oui, couper l'accès</button>
                        <button type="button" className="btn" onClick={() => setLien((x) => ({ ...x, question: null }))}>Annuler</button>
                      </div>
                    </div>
                  )}
                  {clientSel && clientSel.isActive === false && (
                    <span className="note warn">Client inactif (enregistré) : son lien ne s'ouvre pas ; activez-le et enregistrez avant de créer un lien.</span>
                  )}
                  {!lien.question && (
                    <div className="row">
                      <button type="button" className="btn" disabled={lien.etat === 'encours' || !clientSel || clientSel.isActive === false}
                        onClick={() => (actifLien ? setLien((x) => ({ ...x, question: 'regenerer', url: null })) : genererLien())}>
                        {lien.etat === 'encours' ? '⏳…' : actifLien ? 'Régénérer le lien client' : 'Créer le lien client'}
                      </button>
                      <button type="button" className="btn" disabled={!actifLien || lien.etat === 'encours'}
                        onClick={() => setLien((x) => ({ ...x, question: 'couper', url: null }))}>
                        Couper l'accès
                      </button>
                    </div>
                  )}
                  {lien.msg && <span className={'note' + (lien.etat === 'err' ? ' err' : '')}>{lien.msg}</span>}
                </fieldset>
              )}
            </fieldset>
          )}
        </section>
      </div>
    </div>
  );
}

// Aperçu renvoyé par le serveur : sujet, destinataires, corps (HTML dans un cadre isolé, sans script).
function Apercu({ data, lang, type }) {
  const liste = (v) => (Array.isArray(v) ? v.join(', ') : v) || '—';
  const tab = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
  const inconnus = [...new Set([...tab(data.inconnus), ...tab(data.reperesInconnus)])];
  const avert = tab(data.avertissements);
  const html = typeof data.html === 'string' ? data.html : null;
  const texte = typeof data.text === 'string' ? data.text : typeof data.body === 'string' ? data.body : null;
  return (
    <div className="apercu">
      <span className="note">{`${TYPES_APERCU[type] || 'Mail'} · ${nomLangue(lang)} · commande exemple`}</span>
      <div><b>Sujet :</b> {data.subject || '—'}</div>
      {data.to !== undefined && <div className="mono petit"><b>À :</b> {liste(data.to)}</div>}
      {data.cc !== undefined && <div className="mono petit"><b>Copie :</b> {liste(data.cc)}</div>}
      {(data.replyTo !== undefined) && <div className="mono petit"><b>Réponse à :</b> {liste(data.replyTo)}</div>}
      {/* Le serveur décrit les repères inconnus dans ses avertissements ; sinon, la liste brute. */}
      {avert.length > 0
        ? avert.map((a) => <div key={a} className="msg warn">⚠ {a}</div>)
        : inconnus.length > 0 && <div className="msg warn">{`Repères inconnus (laissés tels quels) : ${inconnus.join(', ')}`}</div>}
      {html ? <iframe title="Aperçu du mail" sandbox="" srcDoc={html} className="cadre" />
        : texte ? <pre className="texte">{texte}</pre> : <span className="note">Pas de corps dans l'aperçu.</span>}
    </div>
  );
}

const CSS = `
.pcl{max-width:1100px;margin:0 auto;padding:.75rem;color:#17232a;font-size:16px;line-height:1.45}
.pcl *{box-sizing:border-box}
.pcl h1{font-size:1.25rem;margin:.25rem 0}
.pcl h2{font-size:1.1rem;margin:0 0 4px;overflow-wrap:anywhere}
.pcl .note{display:block;font-size:.86rem;color:#5b6b73;overflow-wrap:anywhere}
.pcl .note.err{color:#a6342b}.pcl .note.warn{color:#965600}.pcl .note.ok{color:#2a7034}
.pcl .mono{font-family:"JetBrains Mono",Consolas,monospace}
.pcl .grille{display:grid;grid-template-columns:minmax(220px,300px) minmax(0,1fr);gap:14px;align-items:start;margin-top:10px}
@media (max-width:760px){.pcl .grille{grid-template-columns:1fr}}
.pcl .liste{display:flex;flex-direction:column;gap:8px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:10px;min-width:0}
.pcl .liste ul{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:4px;max-height:70vh;overflow:auto}
.pcl .ligne{width:100%;text-align:left;border:1px solid #e2e8f0;border-radius:8px;background:#fff;padding:8px 10px;cursor:pointer;font:inherit;display:flex;flex-direction:column;gap:4px;min-height:44px;color:#17232a}
.pcl .ligne.sel{border-color:#0f6e74;background:#d7ecec}
.pcl .tags{display:flex;gap:4px;flex-wrap:wrap}
.pcl .tag{font-size:.72rem;font-weight:700;border-radius:999px;padding:1px 8px;background:#e2e8f0;color:#334155}
.pcl .tag.vert{background:#dcefdc;color:#2a7034}.pcl .tag.gris{background:#f1f5f9;color:#64748b}.pcl .tag.bleu{background:#dbeafe;color:#1e40af}
.pcl .fiche{display:flex;flex-direction:column;gap:10px;min-width:0}
.pcl .pcl-form{border:0;padding:0;margin:0;min-width:0;display:flex;flex-direction:column;gap:10px}
.pcl .bloc{border:1px solid #cfd8dc;border-radius:12px;background:#f7faf9;padding:10px 12px;display:flex;flex-direction:column;gap:6px;margin:0;min-width:0}
.pcl .bloc legend{font-weight:800;padding:0 4px}
.pcl label,.pcl .lbl{font-size:.8rem;font-weight:700;color:#5b6b73}
.pcl .field{display:flex;flex-direction:column;gap:4px}
.pcl .ligne-adresse{display:flex;gap:6px;align-items:center}
.pcl .ligne-adresse input{flex:1;min-width:0}
.pcl .btn.ajouter{align-self:flex-start}
.pcl input[type=text],.pcl input[type=date],.pcl select,.pcl textarea{font:inherit;font-size:16px;color:#17232a;background:#fff;border:1px solid #cfd8dc;border-radius:8px;padding:8px 10px;width:100%;min-width:0;min-height:44px}
.pcl textarea{font-family:"JetBrains Mono",Consolas,monospace;font-size:14px;resize:vertical}
.pcl input.bad,.pcl select.bad,.pcl .modele.bad textarea{border-color:#a6342b;background:#fdf1ef}
.pcl input:focus-visible,.pcl textarea:focus-visible,.pcl button:focus-visible{outline:3px solid #0f6e74;outline-offset:2px}
.pcl .case{display:flex;gap:8px;align-items:center;font-size:.92rem;font-weight:600;color:#17232a;min-height:36px;cursor:pointer}
.pcl .case input{width:20px;height:20px;flex:0 0 auto}
.pcl .case.grise{opacity:.55;cursor:not-allowed}
.pcl .langues{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:4px}
.pcl .langues li{display:flex;justify-content:space-between;align-items:center;gap:8px;background:#fff;border:1px solid #e2e8f0;border-radius:8px;padding:2px 8px}
.pcl .fleches{display:flex;gap:4px}
.pcl .modele{border:1px dashed #cfd8dc;border-radius:10px;padding:8px 10px;display:flex;flex-direction:column;gap:4px;margin:4px 0 0;background:#fff;min-width:0}
.pcl .modele legend{font-weight:700;font-size:.9rem;padding:0 4px}
.pcl .etat-modele{font-size:.8rem;font-weight:700}
.pcl .etat-modele.defaut{color:#5b6b73}.pcl .etat-modele.perso{color:#0f6e74}.pcl .etat-modele.manque{color:#a6342b}
.pcl .aide{font-size:.86rem}.pcl .aide ul{margin:4px 0;padding-left:18px}
.pcl .row{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
.pcl .row input{flex:1 1 220px}
.pcl .barre{position:sticky;bottom:0;background:#fff;padding:8px 0;border-top:1px solid #e2e8f0;z-index:1}
.pcl .btn{font:inherit;font-weight:700;border-radius:10px;border:1px solid #cfd8dc;background:#fff;color:#17232a;padding:10px 14px;cursor:pointer;min-height:44px}
.pcl .btn.primary{background:#0f6e74;border-color:#0f6e74;color:#fff}
.pcl .btn.small{padding:6px 10px;font-size:.88rem;font-weight:600;min-height:40px}
.pcl .btn.danger{border-color:#a6342b;color:#a6342b;background:#f8dedb}
.pcl .btn:disabled,.pcl .btn.inactif{opacity:.45;cursor:not-allowed}
.pcl .filtre{min-height:40px}
.pcl .question{display:flex;flex-direction:column;gap:8px;padding:10px;border-radius:10px;background:#fbecd3;color:#965600;font-weight:600}
.pcl .msg{padding:8px 10px;border-radius:8px;font-size:.9rem;overflow-wrap:anywhere}
.pcl .msg.warn{background:#fbecd3;color:#965600}.pcl .msg.err{background:#f8dedb;color:#a6342b}
.pcl .msg ul{margin:4px 0 0;padding-left:18px}
.pcl .lien{display:flex;flex-direction:column;gap:6px;padding:10px;border-radius:10px;border:2px solid #0f6e74;background:#d7ecec}
.pcl .apercu{display:flex;flex-direction:column;gap:6px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:10px;min-width:0}
.pcl .petit{font-size:.85rem;overflow-wrap:anywhere}
.pcl .cadre{width:100%;min-height:420px;border:1px solid #cfd8dc;border-radius:8px;background:#fff}
.pcl .texte{white-space:pre-wrap;font-size:.85rem;background:#f8fafc;padding:8px;border-radius:8px;overflow-wrap:anywhere}
`;
